import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Edit2, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useCoachesSupabaseStore } from "@/store/api/coaches";
import { useAccountingMutations, useExpenses } from "@/hooks/useAccounting";
import { formatAccountingAmount } from "@/lib/accounting/format";
import type { Expense, ExpenseKind } from "@/services/edgeFunctionWrappers/accountingManagementWrapper";
import { ExpenseFormDialog } from "./ExpenseFormDialog";

interface Props {
  period: { start_date: string; end_date: string };
  /** Mois (yyyy-MM) visé par la génération des récurrences. Par défaut, le
   *  mois de début de période — utile quand la période couvre une année. */
  generationMonth?: string;
}

/** Libellé lisible d'une écriture : bénéficiaire (salaire) ou libellé. */
const displayName = (e: Expense) => e.employee_name || e.label;

/**
 * Onglet Dépenses : trois zones — les modèles récurrents (tous types), les
 * dépenses fournisseur de la période et les frais ponctuels de la période.
 * Les échéances générées vivent dans l'onglet « Échéances ».
 */
export function RecurringExpensesTab({ period, generationMonth }: Props) {
  const { t } = useTranslation(["accounting", "common"]);
  const { toast } = useToast();
  const { currency } = useCurrency();
  const fmt = (v: number) => formatAccountingAmount(v, currency);

  const { coaches, fetchCoaches } = useCoachesSupabaseStore();
  useEffect(() => { fetchCoaches(); }, [fetchCoaches]);
  const coachOptions = useMemo(
    () => (coaches ?? []).map((c) => ({ id: c.id, name: `${c.firstName} ${c.lastName}`.trim(), hourlyRate: c.hourlyRate })),
    [coaches]
  );

  // Deux requêtes ciblées (pas tout l'historique) : les modèles récurrents
  // d'un côté, les écritures de la période sélectionnée de l'autre.
  const { data: templatesRaw = [], isLoading } = useExpenses({ templates_only: true });
  const templates = useMemo(
    () => templatesRaw.filter((e) => e.status !== "cancelled"),
    [templatesRaw]
  );
  const templateIds = useMemo(() => new Set(templates.map((tpl) => tpl.id)), [templates]);
  const { data: occurrences = [], isLoading: loadingPeriod } = useExpenses({
    start_date: period.start_date,
    end_date: period.end_date,
  });
  // Saisie ponctuelle de la période : ni modèle récurrent, ni échéance générée.
  const oneOff = useMemo(
    () => occurrences.filter((o) => !o.recurring_source_id && !templateIds.has(o.id)),
    [occurrences, templateIds]
  );
  const supplierExpenses = useMemo(() => oneOff.filter((o) => o.kind === "supplier_invoice"), [oneOff]);
  const fees = useMemo(() => oneOff.filter((o) => o.kind !== "supplier_invoice"), [oneOff]);

  const { markPaid, updateExpense } = useAccountingMutations();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [formKind, setFormKind] = useState<ExpenseKind>("expense");
  const [formRecurring, setFormRecurring] = useState(false);
  const [disabling, setDisabling] = useState<Expense | null>(null);

  const month = generationMonth ?? period.start_date.slice(0, 7);

  const openCreate = (kind: ExpenseKind, recurring: boolean) => {
    setEditing(null);
    setFormKind(kind);
    setFormRecurring(recurring);
    setFormOpen(true);
  };

  const openEdit = (e: Expense) => {
    setEditing(e);
    setFormKind(e.kind);
    setFormRecurring(false);
    setFormOpen(true);
  };

  const onMarkPaid = async (e: Expense) => {
    try {
      await markPaid.mutateAsync({ id: e.id });
      toast({ title: t("journal.markedPaid") });
    } catch (err: any) {
      toast({ title: t("common:error"), description: err?.message ?? "", variant: "destructive" });
    }
  };

  // Désactiver un modèle = status cancelled (l'historique des occurrences reste).
  const onDisable = async () => {
    if (!disabling) return;
    try {
      await updateExpense.mutateAsync({ id: disabling.id, patch: { status: "cancelled" } });
      toast({ title: t("recurringTab.templateDisabled") });
      setDisabling(null);
    } catch (e: any) {
      toast({ title: t("common:error"), description: e?.message ?? "", variant: "destructive" });
    }
  };

  const row = (e: Expense, opts: { template?: boolean }) => (
    <div
      key={e.id}
      className="flex flex-col gap-2 rounded-xl border p-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="min-w-0">
        <div className="font-medium truncate">{displayName(e)}</div>
        <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1">
          <Badge variant="outline" className="font-normal">{t(`kinds.${e.kind}`)}</Badge>
          <span>
            {opts.template
              ? t("recurringTab.monthlyOn", { day: e.expense_date.slice(8, 10) })
              : e.expense_date}
          </span>
          {e.suppliers?.name ? <span className="truncate">{e.suppliers.name}</span> : null}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 sm:justify-end sm:shrink-0">
        <span className="font-semibold tabular-nums">{fmt(Number(e.amount))}</span>
        <div className="flex items-center gap-1 sm:gap-2">
          {!opts.template && (e.status === "paid"
            ? <Badge variant={"success" as any}>{t("statuses.paid")}</Badge>
            : (
              <Button variant="outline" size="sm" className="h-9" onClick={() => onMarkPaid(e)}>
                <CheckCircle2 className="h-4 w-4 sm:mr-1.5 text-emerald-600" />
                <span className="hidden sm:inline">{t("journal.markPaid")}</span>
              </Button>
            ))}
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => openEdit(e)} aria-label={t("common:edit", { defaultValue: "Modifier" })}>
            <Edit2 className="h-4 w-4" />
          </Button>
          {opts.template && (
            <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setDisabling(e)} aria-label={t("recurringTab.disable")}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  const section = (
    title: string,
    addLabel: string,
    emptyLabel: string,
    rows: Expense[],
    onAdd: () => void,
    loading: boolean,
    template = false
  ) => (
    <Card>
      <CardHeader className="flex flex-col items-start gap-2 pb-2 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle className="text-base min-w-0 truncate">{title}</CardTitle>
        <Button size="sm" className="h-9 w-full sm:w-auto" onClick={onAdd}>
          <Plus className="h-4 w-4 mr-1.5" /> {addLabel}
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <Skeleton className="h-16 rounded-xl" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-3 text-center">{emptyLabel}</p>
        ) : (
          rows.map((e) => row(e, { template }))
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4">
      {section(
        t("recurringTab.templates"),
        t("recurringTab.add"),
        t("recurringTab.noTemplates"),
        templates,
        () => openCreate("expense", true),
        isLoading,
        true
      )}

      {section(
        t("recurringTab.supplierExpenses"),
        t("recurringTab.addSupplierExpense"),
        t("recurringTab.noSupplierExpenses"),
        supplierExpenses,
        () => openCreate("supplier_invoice", false),
        loadingPeriod
      )}

      {section(
        t("recurringTab.fees"),
        t("recurringTab.addFee"),
        t("recurringTab.noFees"),
        fees,
        () => openCreate("expense", false),
        loadingPeriod
      )}

      <ExpenseFormDialog
        key={`${editing?.id ?? "new"}-${formKind}-${formRecurring}`}
        open={formOpen}
        onOpenChange={(o) => { setFormOpen(o); if (!o) setEditing(null); }}
        expense={editing}
        defaultKind={formKind}
        defaultRecurring={formRecurring}
        coaches={coachOptions}
        periodMonth={month}
      />

      <AlertDialog open={!!disabling} onOpenChange={(o) => !o && setDisabling(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("recurringTab.confirmDisable")}</AlertDialogTitle>
            <AlertDialogDescription>{t("recurringTab.confirmDisableDesc", { name: disabling ? displayName(disabling) : "" })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={onDisable}>{t("recurringTab.disable")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
